import { InfoIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Badge, Image, TextField } from '@/components';
import { Label } from '@/components/ui/Label';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { MAX_PROMPT_TEXT_LENGTH } from '@/types/report';
import type { ReportEventThumbnailFrame } from '@/types/report';
import { thumbnailFrameUrl } from '@/utils/report-event-thumbnails';
import { ManageReportEventThumbnailsPopover } from './ManageReportEventThumbnailsPopover';

interface ReportEventEditorFieldsProps {
    sourceId: string;
    thumbnails: ReportEventThumbnailFrame[];
    promptText: string;
    description: string;
    promptTextError?: string;
    addThumbnailDisabled: boolean;
    managePopoverOpen: boolean;
    onManagePopoverOpenChange: (open: boolean) => void;
    onPromptTextChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    onThumbnailsChange: (next: ReportEventThumbnailFrame[]) => void;
    onStartAddThumbnail: () => void;
}

export function ReportEventEditorFields({
    sourceId,
    thumbnails,
    promptText,
    description,
    promptTextError,
    addThumbnailDisabled,
    managePopoverOpen,
    onManagePopoverOpenChange,
    onPromptTextChange,
    onDescriptionChange,
    onThumbnailsChange,
    onStartAddThumbnail,
}: ReportEventEditorFieldsProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);

    const primaryThumbnail = thumbnails[0];

    return (
        <div className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1">
                    <Label className="cursor-default">
                        {t('eventEditor.thumbnailsLabel')}
                    </Label>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className="inline-flex text-grey-300 hover:text-white-100"
                                aria-label={t('eventEditor.thumbnailsHint')}
                            >
                                <InfoIcon
                                    className="h-3.5 w-3.5"
                                    weight="fill"
                                />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={8}>
                            <p className="max-w-xs">
                                {t('eventEditor.thumbnailsHint')}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </div>
                <div className="group relative isolate h-[100px] w-[154px] shrink-0 overflow-hidden rounded-[4px] bg-grey-600">
                    {primaryThumbnail ? (
                        <Image
                            src={thumbnailFrameUrl(sourceId, primaryThumbnail)}
                            alt=""
                            auth
                            generatingPreview
                            className="relative z-0 h-full w-full object-cover"
                        />
                    ) : (
                        <div className="relative z-0 h-full w-full" />
                    )}
                    <ManageReportEventThumbnailsPopover
                        open={managePopoverOpen}
                        onOpenChange={onManagePopoverOpenChange}
                        sourceId={sourceId}
                        thumbnails={thumbnails}
                        addThumbnailDisabled={addThumbnailDisabled}
                        onThumbnailsChange={onThumbnailsChange}
                        onStartAddThumbnail={onStartAddThumbnail}
                    />
                    <span className="pointer-events-none absolute bottom-1 right-1 z-[3] flex items-center">
                        <Badge
                            variant="number"
                            animated={false}
                            value={thumbnails.length}
                        />
                    </span>
                </div>
            </div>
            <TextField
                label={t('eventEditor.eventNameLabel')}
                value={promptText}
                onChange={(event) => onPromptTextChange(event.target.value)}
                maxLength={MAX_PROMPT_TEXT_LENGTH}
                error={promptTextError}
                required
            />
            <TextField
                variant="area"
                label={t('eventEditor.descriptionLabel')}
                placeholder={t('eventEditor.descriptionPlaceholder')}
                value={description}
                onChange={(event) => onDescriptionChange(event.target.value)}
            />
        </div>
    );
}
